import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SidebarContext } from '../../context/SidebarContext.js';
import SectionMenuLeft from '../../components/SectionMenuLeft/SectionMenuLeft.jsx';
import "./payouts.css";
import { toast, ToastContainer } from 'react-toastify';
import { config } from '../../config.js';
import ReactTable from '../../components/ReactTable/ReactTable.jsx';
import { FaCheck } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { LuArrowLeft } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

/* ---------------- TABLE SKELETON ---------------- */
const TableSkeleton = ({ rows = 6 }) => (
    <div className="table-skeleton">
        {[...Array(rows)].map((_, i) => (
            <div className="skeleton-row" key={i}>
                <div className="skeleton-cell" />
                <div className="skeleton-cell" />
                <div className="skeleton-cell" />
                <div className="skeleton-cell" />
                <div className="skeleton-cell" />
            </div>
        ))}
    </div>
);

const DriverPayouts = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("adminToken");
    const { isSidebar } = useContext(SidebarContext);
    const [loading, setLoading] = useState(false);
    const [payoutList, setPayoutList] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [actionLoading, setActionLoading] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const role = localStorage.getItem("adminRole");
    const adminCity = role === "sub-admin" ? localStorage.getItem("adminCity") : null;

    /* ---------------- FETCH PAYOUTS ---------------- */
    const fetchPayouts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${config.backEndBaseUrl}payout/list${adminCity ? `?acd=${adminCity.toLocaleLowerCase()}` : ""}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        "Authorization": `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error();

            const result = await response.json();
            setPayoutList(Array.isArray(result.data) ? result.data : []);
        } catch (err) {
            console.error(err);
            setPayoutList([]);
        } finally {
            setLoading(false);
        }
    }, [adminCity, token]);

    useEffect(() => {
        fetchPayouts();
    }, [fetchPayouts]);

    /* ---------------- TABS ---------------- */
    const TABS = [
        { key: 'all', label: 'All' },
        { key: 'requested', label: 'Requested' },
        { key: 'processing', label: 'Processing' },
        { key: 'approved', label: 'Approved' },
        { key: 'failed', label: 'Failed' },
        { key: 'rejected', label: 'Rejected' },
    ];

    /* ---------------- FILTERED DATA ---------------- */
    const filteredPayoutList = useMemo(() => {
        if (activeTab === 'all') return payoutList;
        return payoutList.filter(p => p.status === activeTab);
    }, [payoutList, activeTab]);


    /* ---------------- HANDLE PAYOUT ---------------- */
    const handlePayoutRequest = useCallback(
        async (payoutId, type) => {
            if (!payoutId || !type) return;

            setActionLoading(true);
            try {
                const response = await fetch(
                    `${config.backEndBaseUrl}payout/request/handle`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            payoutId,
                            type, // approve | reject
                        }),
                    }
                );


                const result = await response.json().catch(() => ({}));
                if (!response.ok) {
                    toast.error(result.message || "Action failed", {
                        className: "big-toast",
                    });
                    throw new Error(result.message || "Action failed");
                }
                if (result.status) {
                    toast.success(result.message, {
                        className: "big-toast",
                    });
                } else {
                    toast.error(result.message, {
                        className: "big-toast",
                    });
                }

                await fetchPayouts(); // refresh list
            } catch (err) {
                console.error(err);
            } finally {
                setActionLoading(false);
            }
        },
        [fetchPayouts]
    );

    /* ---------------- TABLE COLUMNS ---------------- */
    const columns = useMemo(() => [
        {
            accessorKey: 'driverName',
            header: 'Driver',
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            Cell: ({ cell }) => {
                const amount = cell.row.original.amount;
                return (
                    <span >
                        {amount?.toFixed(2)}
                    </span>
                );
            },
        },
        {
            accessorKey: 'walletBalance',
            header: 'Wallet Balance',
            Cell: ({ cell }) => {
                const walletBalance = cell.row.original.walletBalance;
                return (
                    <span >
                        {walletBalance?.toFixed(2)}
                    </span>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => {
                const status = cell.row.original.status;

                const statusClassMap = {
                    requested: 'badge-warning',
                    processing: 'badge-processing',
                    approved: 'badge-success',
                    failed: 'badge-danger',
                    rejected: 'badge-danger',
                };

                return (
                    <span className={`badge ${statusClassMap[status] || 'badge-secondary'}`}>
                        {status}
                    </span>
                );
            },
        },
        {
            accessorKey: 'id',
            header: 'Actions',
            Cell: ({ cell }) => {
                const { id: payoutId, status } = cell.row.original;

                if (status !== "requested") return null;

                return (
                    <div className="flex gap-2">
                        <button
                            className="btn btn-success"
                            disabled={actionLoading}
                            onClick={() =>
                                setConfirmData({ payoutId, type: "approve" })
                            }
                        >
                            <FaCheck />
                        </button>

                        <button
                            className="btn btn-danger"
                            disabled={actionLoading}
                            onClick={() =>
                                setConfirmData({ payoutId, type: "reject" })
                            }
                        >
                            <ImCross />
                        </button>
                    </div>
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Requested',
        },
        {
            accessorKey: 'updatedAt',
            header: 'Last Updated',
        },
    ], []);




    return (
        <div className="body">
            <div id="wrapper">
                <div id="page">
                    <div className="layout-wrap">
                        <SectionMenuLeft />

                        <div className="section-content-right">
                            <div
                                className="main-content"
                                style={{ paddingLeft: isSidebar ? '280px' : 0 }}
                            >
                                <div className="main-content-inner">
                                    <ToastContainer />

                                    <div className="wg-box">
                                        {/* Header */}
                                         <div className="flex items-center justify-between mb-4 gap-3">
                                                                <div className="flex items-center gap-3">
                                                                  <button
                                                                    className="back-btn flex items-center gap-2"
                                                                    onClick={() => navigate("/")}
                                                                  >
                                                                    <LuArrowLeft size={16} />
                                                                    {/* <span>Back</span> */}
                                                                  </button>
                                        
                                                                  <h5 className="fare-heading">Driver Payouts</h5>
                                                                </div>
                                                              </div>

                                        {/* Tabs */}
                                        <div className="payout-tabs">
                                            {TABS.map(tab => (
                                                <button
                                                    key={tab.key}
                                                    className={`payout-tab ${activeTab === tab.key ? 'active' : ''}`}
                                                    onClick={() => setActiveTab(tab.key)}
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Table */}
                                        {loading ? (
                                            <TableSkeleton />
                                        ) : (
                                            <ReactTable
                                                columns={columns}
                                                data={filteredPayoutList}
                                                disabled={actionLoading}
                                            />
                                        )}

                                        {confirmData && (
                                            <ConfirmModal
                                                type={confirmData.type}
                                                loading={actionLoading}
                                                onCancel={() => setConfirmData(null)}
                                                onConfirm={async () => {
                                                    await handlePayoutRequest(
                                                        confirmData.payoutId,
                                                        confirmData.type
                                                    );
                                                    setConfirmData(null);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


const ConfirmModal = ({ type, onCancel, onConfirm, loading }) => {
    const isApprove = type === "approve";

    return (
        <div className="modal-backdrop">
            <div className="modal-card" style={{ maxWidth: 420 }}>
                <div className="modal-header">
                    <h3 className="modal-title">
                        {isApprove ? "Approve Payout" : "Reject Payout"}
                    </h3>
                </div>

                <div className="modal-body">
                    <p>
                        Are you sure you want to{" "}
                        <strong>{isApprove ? "approve" : "reject"}</strong>{" "}
                        this payout request?
                    </p>

                    {!isApprove && (
                        <p className="text-danger">
                            This action cannot be undone.
                        </p>
                    )}
                </div>

                <div className="modal-footer">
                    <button disabled={loading} onClick={onCancel} className="btn btn-dark">
                        Cancel
                    </button>

                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={`btn ${isApprove ? "btn-success" : "btn-danger"
                            }`}
                    >
                        {loading
                            ? "Processing..."
                            : isApprove
                                ? "Approve"
                                : "Reject"}
                    </button>
                </div>
            </div>
        </div>
    );
};


export default DriverPayouts;
